import { Link } from "react-router-dom";
import { TodaySection } from "./today-section";
import { EmptyState } from "@/components/v2/empty-state";
import { useFollowUps } from "@/hooks/use-api";

// ADV-799: Follow-ups Needed section. The /core/follow-ups response is a
// discriminated union — invoice rows carry `due_date`, opportunity rows
// `stale_since`. Both render through this single component.

interface InvoiceFollowUpRow {
  source: "invoice";
  id: string;
  label: string;
  secondary: string | null;
  due_date: string;
  amount: number | null;
}
interface OpportunityFollowUpRow {
  source: "opportunity";
  id: string;
  label: string;
  secondary: string | null;
  stale_since: string;
  amount: number | null;
}
type FollowUpRow = InvoiceFollowUpRow | OpportunityFollowUpRow;

interface FollowUpsResponse {
  follow_ups?: FollowUpRow[];
  total?: number;
}

function SkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <ul className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <li
          key={i}
          className="h-9 animate-pulse rounded-md bg-[color:var(--color-surface-2)]"
        />
      ))}
    </ul>
  );
}

function destinationFor(row: FollowUpRow): string {
  // No `/invoices/:id` detail route exists yet (App.tsx only registers the
  // `/invoices` list — see ADV-799 PR #968 Codex review). Send invoice rows
  // to the list until a detail page lands; opportunities already have one.
  return row.source === "invoice" ? `/invoices` : `/opportunities/${row.id}`;
}

function ageLabelFor(row: FollowUpRow): string {
  const dateString = row.source === "invoice" ? row.due_date : row.stale_since;
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

interface SectionProps {
  title?: string;
}

export function FollowUpsSection({
  title = "Follow-ups needed",
}: SectionProps = {}) {
  const { data, isLoading, isError } = useFollowUps(25);
  const response = data as FollowUpsResponse | undefined;
  const rows = response?.follow_ups ?? [];

  return (
    <TodaySection
      id="today-section-follow-ups"
      title={title}
      count={response ? rows.length : undefined}
    >
      {isLoading ? (
        <SkeletonRows />
      ) : isError ? (
        <EmptyState
          title="Couldn't load follow-ups"
          description="Try refreshing the page."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="You're all caught up"
          description="No overdue invoices or stale opportunities right now."
        />
      ) : (
        <ul className="divide-y divide-[color:var(--color-border)]">
          {rows.map((row) => (
            <li
              key={`${row.source}-${row.id}`}
              className="flex items-center justify-between gap-2 py-2 text-sm"
            >
              <Link
                to={destinationFor(row)}
                className="flex-1 font-medium text-[color:var(--color-text-hi)] hover:underline"
              >
                {row.label}
              </Link>
              <span className="text-xs uppercase tracking-wide text-[color:var(--color-text-md)]">
                {row.source}
              </span>
              <span className="tabular-nums text-[color:var(--color-text-md)]">
                {ageLabelFor(row)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </TodaySection>
  );
}
